import React from "react";
import {
  getStoredWebPushSubscriptionId,
  setStoredWebPushSubscriptionId,
} from "../util";

const NotificationContext = React.createContext(undefined);

const getVapidKey = () =>
  typeof document !== "undefined"
    ? document.querySelector('meta[name="vapid-public-key"]')?.content || null
    : null;

const getBuildingCode = () =>
  typeof window !== "undefined"
    ? window.location.pathname.match(/\/hebe\/([^/]+)\/hgs/)?.[1] || null
    : null;

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const NotificationProvider = ({ children }) => {
  const [notificationState, setNotificationState] = React.useState("loading");

  React.useEffect(() => {
    const check = async () => {
      if (
        typeof window === "undefined" ||
        !("Notification" in window) ||
        !("serviceWorker" in navigator)
      ) {
        setNotificationState("unsupported");
        return;
      }

      const vapidKey = getVapidKey();
      if (!vapidKey) {
        setNotificationState("unsupported");
        return;
      }

      const permission = Notification.permission;
      if (permission === "denied") {
        setNotificationState("denied");
        return;
      }

      if (permission === "default") {
        setNotificationState("prompt");
        return;
      }

      // permission === "granted" — check if an active subscription exists
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        const buildingCode = getBuildingCode();
        const storedId = getStoredWebPushSubscriptionId(buildingCode);

        if (subscription && storedId) {
          setNotificationState("subscribed");
        } else {
          setNotificationState("unsubscribed");
        }
      } catch (_e) {
        setNotificationState("unsubscribed");
      }
    };

    check();
  }, []);

  const subscribe = React.useCallback(async () => {
    const vapidKey = getVapidKey();
    const buildingCode = getBuildingCode();

    if (!vapidKey || !("Notification" in window) || !("serviceWorker" in navigator)) {
      return null;
    }

    try {
      let permission = Notification.permission;
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }

      if (permission !== "granted") {
        setNotificationState("denied");
        return null;
      }

      const registration = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }

      const json = subscription.toJSON();
      const response = await fetch("/api/v1/web_push_subscriptions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          building_code: buildingCode,
          endpoint: json.endpoint,
          p256dh_key: json.keys?.p256dh,
          auth_key: json.keys?.auth,
        }),
      });

      const payload = await response.json();
      const id = payload?.data?.id ?? null;
      if (id && buildingCode) {
        setStoredWebPushSubscriptionId(buildingCode, id);
      }

      setNotificationState("subscribed");
      return id;
    } catch (e) {
      console.warn("[NotificationProvider] subscribe failed:", e);
      // Re-check permission in case it was denied during the flow
      if (Notification.permission === "denied") {
        setNotificationState("denied");
      }
      return null;
    }
  }, []);

  const getSubscriptionId = React.useCallback(() => {
    const buildingCode = getBuildingCode();
    return getStoredWebPushSubscriptionId(buildingCode);
  }, []);

  const contextValue = React.useMemo(
    () => ({
      notificationState,
      subscribe,
      getSubscriptionId,
    }),
    [notificationState, subscribe, getSubscriptionId],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = React.use(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};
